/**
 * Paw Parents Quiz - Simplified & Production-Ready
 */

(function() {
  'use strict';

  function initPawParentsQuiz() {
    const quizContainers = document.querySelectorAll('[id^="PawParentsQuiz-"]');
    
    quizContainers.forEach(function(container) {
      // Configuration
      const variantId = container.dataset.variantId || '47525292605540';
      const sellingPlans = {
        'monthly': container.dataset.sellingPlanMonthly || '',
        '6-month': container.dataset.sellingPlan6month || '',
        '12-month': container.dataset.sellingPlan12month || ''
      };
      
      // State
      let quizData = {
        petType: '',
        answers: {},
        email: '',
        selectedPlan: '',
        sellingPlanId: '',
        originalSellingPlanId: '' // Store original plan from cart for comparison
      };
      let existingCartItem = null;
      let cartItemKey = null;
      let currentStepIndex = 0;
      let isEditMode = false; // Explicit edit mode flag
      let isSubmitting = false; // Prevent duplicate submissions
      let autoUpdateTimeout = null; // For optional debounced auto-update (disabled by default)
      
      const quizSteps = Array.from(container.querySelectorAll('.quiz-step'));
      const progressBar = container.querySelector('#quiz-progress-bar-' + container.id.replace('PawParentsQuiz-', ''));
      
      // Safely resolve selling plan id from a cart line item
      function getItemSellingPlanId(item) {
        if (!item) return '';
        const allocationId = item?.selling_plan_allocation?.selling_plan?.id;
        const directId = item?.selling_plan_id || item?.selling_plan;
        const id = allocationId || directId || '';
        return id ? String(id).trim() : '';
      }
      
      // Load from localStorage
      const saved = localStorage.getItem('pawParentsQuizData');
      if (saved) {
        try {
          quizData = { ...quizData, ...JSON.parse(saved) };
        } catch (e) {
          console.error('Error loading quiz data:', e);
        }
      }
      
      // Utility: Save to localStorage
      function save() {
        localStorage.setItem('pawParentsQuizData', JSON.stringify(quizData));
      }
      
      // Utility: Get active steps based on pet type
      function getActiveSteps() {
        if (!quizData.petType) {
          return quizSteps.filter(s => s.dataset.quizType === 'selection');
        }
        return quizSteps.filter(s => {
          const type = s.dataset.quizType;
          return type === quizData.petType || type === 'both' || type === 'selection';
        });
      }
      
      // Utility: Find step by data-step attribute
      function findStep(stepId) {
        return quizSteps.find(s => s.dataset.step === stepId);
      }
      
      // Utility: Get step index
      function getStepIndex(stepId) {
        return quizSteps.findIndex(s => s.dataset.step === stepId);
      }
      
      // Show specific step
      function showStep(stepIndex) {
        if (stepIndex < 0 || stepIndex >= quizSteps.length) return;
        
        const targetStep = quizSteps[stepIndex];
        const activeSteps = getActiveSteps();
        
        // Check if step should be shown
        if (!activeSteps.includes(targetStep) && targetStep.dataset.step !== 'plan-selection') {
          return;
        }
        
        // Hide all, show target
        quizSteps.forEach((step, i) => {
          step.classList.toggle('active', i === stepIndex);
        });
        
        currentStepIndex = stepIndex;
        updateProgress();
        populateStep(targetStep);
        updatePetNamePlaceholders();
        
        // Also populate email and plan when showing any step (in case they're in inactive steps)
        populateEmail();
        populatePlan();
        
        // Update submit button text if in edit mode
        if (isEditMode) {
          const submitBtn = container.querySelector('.quiz-button--submit');
          if (submitBtn) {
            submitBtn.textContent = 'Update Cart';
            if (quizData.selectedPlan) submitBtn.disabled = false;
          }
        }
      }
      
      // Update progress bar
      function updateProgress() {
        if (!progressBar) return;
        const activeSteps = getActiveSteps();
        const currentActiveIndex = activeSteps.indexOf(quizSteps[currentStepIndex]);
        const progress = activeSteps.length > 0 ? ((currentActiveIndex + 1) / activeSteps.length) * 100 : 0;
        progressBar.style.width = progress + '%';
      }
      
      // Update pet name placeholders
      function updatePetNamePlaceholders() {
        const name = quizData.answers[quizData.petType + '-1'] || '';
        const defaultName = quizData.petType === 'dog' ? 'your pup' : 'your kitty';
        container.querySelectorAll('.pet-name-placeholder').forEach(el => {
          el.textContent = name || defaultName;
        });
      }
      
      // Populate a step with saved data
      function populateStep(step) {
        if (!step) return;
        const stepId = step.dataset.step;
        
        // Text inputs
        step.querySelectorAll('input[type="text"], input[type="date"], textarea').forEach(input => {
          const name = input.name || input.id;
          const key = stepId;
          const notesKey = key + '_notes';
          
          if (name.includes('_notes') && quizData.answers[notesKey]) {
            input.value = quizData.answers[notesKey];
          } else if (quizData.answers[key]) {
            input.value = quizData.answers[key];
          }
          
          // Enable next button if required field has value
          if (input.hasAttribute('required') && input.value.trim()) {
            const nextBtn = step.querySelector('.quiz-button--next');
            if (nextBtn) nextBtn.disabled = false;
          }
        });
        
        // Email
        if (stepId === 'dog-9' || stepId === 'cat-8') {
          const emailInput = step.querySelector('input[type="email"]');
          if (emailInput && quizData.email) {
            emailInput.value = quizData.email;
            // Enable next button if email exists
            const nextBtn = step.querySelector('.quiz-button--next');
            if (nextBtn && emailInput.value.trim()) nextBtn.disabled = false;
          }
        }
        
        // Option buttons
        step.querySelectorAll('.quiz-option:not(.quiz-option--pet)').forEach(btn => {
          const value = btn.dataset.value;
          if (quizData.answers[stepId] === value) {
            btn.classList.add('selected');
            const nextBtn = step.querySelector('.quiz-button--next');
            if (nextBtn) nextBtn.disabled = false;
            
            // Show conditional fields
            const conditionalId = btn.dataset.showConditional;
            if (conditionalId) {
              const conditional = container.querySelector('#' + conditionalId);
              if (conditional) conditional.style.display = 'block';
            }
          }
        });
        
        // Checkboxes
        step.querySelectorAll('input[type="checkbox"]').forEach(cb => {
          const stepId = step.dataset.step;
          const arr = quizData.answers[stepId];
          if (Array.isArray(arr) && arr.includes(cb.value)) {
            cb.checked = true;
          }
          // Enable next if any checkbox is checked
          if (cb.checked) {
            const nextBtn = step.querySelector('.quiz-button--next');
            if (nextBtn) nextBtn.disabled = false;
          }
        });
        
        // Plan selection
        if (stepId === 'plan-selection' && quizData.selectedPlan) {
          const planCard = step.querySelector(`.quiz-plan[data-plan="${quizData.selectedPlan}"]`);
          if (planCard) {
            step.querySelectorAll('.quiz-plan').forEach(p => p.classList.remove('selected'));
            planCard.classList.add('selected');
            const submitBtn = container.querySelector('.quiz-button--submit');
            if (submitBtn) submitBtn.disabled = false;
          }
        }
      }
      
      // Populate email in all email inputs (even if step is not active)
      function populateEmail() {
        if (!quizData.email) return;
        container.querySelectorAll('input[type="email"]').forEach(input => {
          const step = input.closest('.quiz-step');
          if (step && (step.dataset.step === 'dog-9' || step.dataset.step === 'cat-8')) {
            if (!input.value) {
              input.value = quizData.email;
            }
          }
        });
      }
      
      // Populate plan selection (even if step is not active)
      function populatePlan() {
        if (!quizData.selectedPlan) return;
        const planCard = container.querySelector(`.quiz-plan[data-plan="${quizData.selectedPlan}"]`);
        if (planCard && !planCard.classList.contains('selected')) {
          container.querySelectorAll('.quiz-plan').forEach(p => p.classList.remove('selected'));
          planCard.classList.add('selected');
          const submitBtn = container.querySelector('.quiz-button--submit');
          if (submitBtn) submitBtn.disabled = false;
        }
      }
      
      // Set edit mode state and update UI
      function setEditMode(enabled) {
        isEditMode = enabled;
        const submitBtn = container.querySelector('.quiz-button--submit');
        
        if (enabled) {
          // Update button text
          if (submitBtn) {
            submitBtn.textContent = 'Update Cart';
          }
          
          // Show helper message
          let helperMsg = container.querySelector('.quiz-edit-mode-message');
          if (!helperMsg) {
            helperMsg = document.createElement('div');
            helperMsg.className = 'quiz-edit-mode-message';
            helperMsg.innerHTML = '<p><strong>You\'re editing an item already in your cart.</strong><br>Click "Update Cart" to save your changes.</p>';
            
            // Insert before plan selection step or at the top of quiz container
            const planStep = container.querySelector('.quiz-step[data-step="plan-selection"]');
            if (planStep) {
              planStep.insertBefore(helperMsg, planStep.firstChild);
            } else {
              const quizContainer = container.querySelector('.quiz__container') || container;
              if (quizContainer.firstChild) {
                quizContainer.insertBefore(helperMsg, quizContainer.firstChild);
              }
            }
          }
          helperMsg.style.display = 'block';
        } else {
          // Remove edit mode UI
          if (submitBtn) {
            submitBtn.textContent = 'Continue to Checkout';
          }
          
          const helperMsg = container.querySelector('.quiz-edit-mode-message');
          if (helperMsg) {
            helperMsg.style.display = 'none';
          }
        }
      }
      
      // Sync form data to quizData
      function syncFormData() {
        // Text inputs
        container.querySelectorAll('input[type="text"], input[type="date"], textarea').forEach(input => {
          const step = input.closest('.quiz-step');
          if (!step) return;
          const stepId = step.dataset.step;
          const name = input.name || input.id;
          
          if (name.includes('_notes')) {
            if (input.value.trim()) {
              quizData.answers[name] = input.value.trim();
            } else {
              delete quizData.answers[name];
            }
          } else if (input.value.trim()) {
            quizData.answers[stepId] = input.value.trim();
          } else if (stepId) {
            delete quizData.answers[stepId];
          }
        });
        
        // Email
        container.querySelectorAll('input[type="email"]').forEach(input => {
          const step = input.closest('.quiz-step');
          if (step && (step.dataset.step === 'dog-9' || step.dataset.step === 'cat-8')) {
            quizData.email = input.value.trim();
          }
        });
        
        // Checkboxes - rebuild from checked state
        container.querySelectorAll('.quiz-step').forEach(step => {
          const stepId = step.dataset.step;
          const checkboxes = step.querySelectorAll('input[type="checkbox"]');
          if (checkboxes.length > 0) {
            const checked = Array.from(checkboxes)
              .filter(cb => cb.checked)
              .map(cb => cb.value)
              .filter((v, i, arr) => arr.indexOf(v) === i); // Remove duplicates
            quizData.answers[stepId] = checked.length > 0 ? checked : undefined;
            if (!quizData.answers[stepId]) delete quizData.answers[stepId];
          }
        });
        
        // Option buttons
        container.querySelectorAll('.quiz-option.selected:not(.quiz-option--pet)').forEach(btn => {
          const step = btn.closest('.quiz-step');
          if (step) {
            quizData.answers[step.dataset.step] = btn.dataset.value;
          }
        });
        
        // Plan
        const selectedPlan = container.querySelector('.quiz-plan.selected');
        if (selectedPlan) {
          quizData.selectedPlan = selectedPlan.dataset.plan;
          quizData.sellingPlanId = selectedPlan.dataset.sellingPlan || sellingPlans[quizData.selectedPlan] || '';
        }
      }
      
      // Extract data from cart properties
      function extractFromCart(cartItem) {
        const props = cartItem.properties || {};
        const data = { petType: '', answers: {}, email: '', selectedPlan: '', sellingPlanId: '' };
        
        // Pet type
        if (props['Pet type']) data.petType = props['Pet type'].toLowerCase();
        
        // Map property names to step IDs
        const propMap = {
          'Pet name': data.petType + '-1',
          'Life stage': data.petType + '-2',
          'Breed': data.petType + '-3',
          'Birthday/Gotcha': data.petType + '-4',
          'Toy preference': 'dog-5',
          'Play style': 'cat-5',
          'Dietary restrictions': 'dog-6',
          'Treat sensitivities': 'cat-6',
          'Harness size': 'dog-7',
          'Collar size': data.petType === 'dog' ? 'dog-8' : 'cat-7'
        };
        
        // Extract simple properties
        Object.keys(propMap).forEach(prop => {
          if (props[prop]) {
            const stepId = propMap[prop];
            if (stepId) {
              if (prop === 'Dietary restrictions' || prop === 'Treat sensitivities') {
                const values = props[prop].split(',').map(s => s.trim().toLowerCase())
                  .filter((v, i, arr) => arr.indexOf(v) === i);
                data.answers[stepId] = values;
              } else {
                const value = props[prop].toLowerCase();
                // Map life stage values
                if (prop === 'Life stage') {
                  if (value.includes('puppy') || value.includes('kitten')) {
                    data.answers[stepId] = data.petType === 'dog' ? 'puppy' : 'kitten';
                  } else if (value.includes('adult')) {
                    data.answers[stepId] = 'adult';
                  } else if (value.includes('senior')) {
                    data.answers[stepId] = 'senior';
                  }
                } else if (prop === 'Toy preference' || prop === 'Play style') {
                  if (value.includes('plush')) data.answers[stepId] = 'plush';
                  else if (value.includes('durable') || value.includes('chew')) data.answers[stepId] = 'durable';
                  else if (value.includes('interactive') || value.includes('chaser')) data.answers[stepId] = 'interactive';
                  else if (value.includes('puzzle')) data.answers[stepId] = 'puzzle';
                  else if (value.includes('mix')) data.answers[stepId] = 'mix';
                } else {
                  data.answers[stepId] = props[prop];
                }
              }
            }
          }
        });
        
        // Notes fields
        ['Dietary notes', 'Sensitivities notes', 'Harness notes', 'Collar notes'].forEach(prop => {
          if (props[prop]) {
            const key = prop.toLowerCase().replace(' notes', '').replace(' ', '_');
            data.answers[data.petType + '-6_notes'] = props[prop];
            if (prop.includes('Harness')) data.answers['dog-7_notes'] = props[prop];
            if (prop.includes('Collar')) data.answers[data.petType === 'dog' ? 'dog-8_notes' : 'cat-7_notes'] = props[prop];
          }
        });
        
        // Email (case-insensitive lookup to handle theme variations)
        // Email (case-insensitive, trim key to avoid whitespace mismatches)
        const emailKey = Object.keys(props || {}).find(k => k && k.trim().toLowerCase() === 'email');
        if (emailKey) data.email = props[emailKey];
        if (props['Selected plan']) {
          const plan = props['Selected plan'].toLowerCase();
          if (plan.includes('one-time')) data.selectedPlan = 'one-time';
          else if (plan.includes('monthly')) data.selectedPlan = 'monthly';
          else if (plan.includes('6-month') || plan.includes('6 month')) data.selectedPlan = '6-month';
          else if (plan.includes('12-month') || plan.includes('12 month')) data.selectedPlan = '12-month';
        }
        
        return data;
      }
      
      // Build cart properties from quizData
      function buildProperties() {
        const props = {};
        const prefix = quizData.petType;
        
        // Labels mapping
        const labels = {
          lifeStage: {
            dog: { puppy: 'Puppy (under 1 year)', adult: 'Adult (1–7 years)', senior: 'Senior (7+ years)' },
            cat: { kitten: 'Kitten (under 1 year)', adult: 'Adult (1–10 years)', senior: 'Senior (10+ years)' }
          },
          toy: {
            dog: { plush: 'Plush toys', durable: 'Durable chew toys', mix: 'A mix of both' },
            cat: { plush: 'Soft plush toys / Snuggler', interactive: 'Interactive toys / Chaser', puzzle: 'Puzzle / brain toys', mix: 'A mix of everything' }
          },
          dietary: { chicken: 'Chicken', turkey: 'Turkey', beef: 'Beef', fish: 'Fish', none: 'None' },
          plan: {
            'one-time': 'One-time delivery',
            'monthly': 'Monthly subscription (billed monthly)',
            '6-month': '6-month subscription (paid upfront)',
            '12-month': '12-month subscription (paid upfront)'
          }
        };
        
        // Basic info
        props['Pet type'] = prefix.charAt(0).toUpperCase() + prefix.slice(1);
        if (quizData.answers[prefix + '-1']) props['Pet name'] = quizData.answers[prefix + '-1'];
        if (quizData.answers[prefix + '-2']) {
          props['Life stage'] = labels.lifeStage[prefix][quizData.answers[prefix + '-2']] || quizData.answers[prefix + '-2'];
        }
        if (quizData.answers[prefix + '-3']) props['Breed'] = quizData.answers[prefix + '-3'];
        if (quizData.answers[prefix + '-4']) props['Birthday/Gotcha'] = quizData.answers[prefix + '-4'];
        
        // Toy preference / Play style
        if (prefix === 'dog' && quizData.answers['dog-5']) {
          props['Toy preference'] = labels.toy.dog[quizData.answers['dog-5']] || quizData.answers['dog-5'];
        } else if (prefix === 'cat' && quizData.answers['cat-5']) {
          props['Play style'] = labels.toy.cat[quizData.answers['cat-5']] || quizData.answers['cat-5'];
        }
        
        // Dietary restrictions / Sensitivities
        const dietaryKey = prefix === 'dog' ? 'dog-6' : 'cat-6';
        if (quizData.answers[dietaryKey]) {
          const arr = Array.isArray(quizData.answers[dietaryKey]) ? quizData.answers[dietaryKey] : [quizData.answers[dietaryKey]];
          const unique = arr.filter((v, i, a) => a.indexOf(v) === i && v !== 'none');
          if (unique.length > 0) {
            props[prefix === 'dog' ? 'Dietary restrictions' : 'Treat sensitivities'] = unique.map(v => labels.dietary[v] || v).join(', ');
          }
        }
        
        // Notes
        if (quizData.answers[dietaryKey + '_notes']) {
          props[prefix === 'dog' ? 'Dietary notes' : 'Sensitivities notes'] = quizData.answers[dietaryKey + '_notes'];
        }
        if (prefix === 'dog' && quizData.answers['dog-7']) props['Harness size'] = quizData.answers['dog-7'];
        if (quizData.answers[prefix === 'dog' ? 'dog-8' : 'cat-7']) {
          props['Collar size'] = quizData.answers[prefix === 'dog' ? 'dog-8' : 'cat-7'];
        }
        if (quizData.answers['dog-7_notes']) props['Harness notes'] = quizData.answers['dog-7_notes'];
        if (quizData.answers[(prefix === 'dog' ? 'dog-8' : 'cat-7') + '_notes']) {
          props['Collar notes'] = quizData.answers[(prefix === 'dog' ? 'dog-8' : 'cat-7') + '_notes'];
        }
        
        // Email and plan (write email even if empty to allow clearing)
        props['Email'] = quizData.email || '';
        if (quizData.selectedPlan) props['Selected plan'] = labels.plan[quizData.selectedPlan] || quizData.selectedPlan;
        
        return props;
      }
      
      // ============================================
      // CART OPERATIONS (Extracted for reusability)
      // ============================================
      
      /**
       * Update existing cart item (properties ONLY - cannot change selling_plan)
       * Uses /cart/change.js because /cart/update.js cannot mutate properties.
       * @param {Object} properties - Cart line item properties
       * @returns {Promise} - Resolves with updated cart
       */
      function updateCartItem(properties) {
        if (!cartItemKey) {
          return Promise.reject(new Error('Cart item key is missing'));
        }
        
        const payload = {
          id: cartItemKey,
          quantity: 1,
          properties: properties
        };
        
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        })
          .then(r => {
            if (!r.ok) {
              return r.json().then(err => {
                console.error('Cart update error response:', err);
                return Promise.reject(new Error(err.message || err.description || 'Update failed'));
              });
            }
            return r.json();
          })
          .then(cart => {
            // Update local references based on returned cart
            const updatedItem = cart.items.find(i => 
              i.variant_id.toString() === variantId.toString() || 
              i.product_handle === 'paw-parents-club-box'
            );
            
            if (updatedItem) {
              existingCartItem = updatedItem;
              cartItemKey = updatedItem.key;
              // Keep original plan tracking aligned with the actual cart item
              const updatedPlanId = getItemSellingPlanId(updatedItem);
              if (updatedPlanId) {
                quizData.originalSellingPlanId = updatedPlanId;
                quizData.sellingPlanId = updatedPlanId;
              } else {
                quizData.originalSellingPlanId = 'one-time';
                quizData.sellingPlanId = '';
              }
              // Sync email from updated cart item back into state/localStorage
              const updatedEmailKey = Object.keys(updatedItem.properties || {}).find(k => k && k.trim().toLowerCase() === 'email');
              if (updatedEmailKey) {
                quizData.email = updatedItem.properties[updatedEmailKey] || '';
              }
              save();
            }
            
            return cart;
          });
      }
      
      /**
       * Remove cart item by setting quantity to 0
       * Uses /cart/update.js with quantity 0 (Shopify standard)
       * @returns {Promise} - Resolves with updated cart
       */
      function removeCartItem() {
        if (!cartItemKey) {
          return Promise.reject(new Error('Cart item key is missing'));
        }
        
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            id: cartItemKey,
            quantity: 0
          })
        })
          .then(r => {
            if (!r.ok) {
              return r.json().then(err => {
                console.error('Cart remove error response:', err);
                return Promise.reject(new Error(err.message || err.description || 'Remove failed'));
              });
            }
            return r.json();
          });
      }
      
      /**
       * Add new item to cart
       * @param {Object} properties - Cart line item properties
       * @param {string} sellingPlanId - Selling plan ID (null for one-time)
       * @returns {Promise} - Resolves with updated cart
       */
      function addCartItem(properties, sellingPlanId) {
        const payload = {
          items: [{
            id: parseInt(variantId, 10),
            quantity: 1,
            properties: properties
          }]
        };
        
        // Add selling plan for subscriptions only
        // CRITICAL: Only add selling_plan if it's not one-time and we have a valid sellingPlanId
        if (quizData.selectedPlan !== 'one-time' && sellingPlanId) {
          const planId = parseInt(sellingPlanId, 10);
          if (!isNaN(planId) && planId > 0) {
            payload.items[0].selling_plan = planId;
          } else {
            console.warn('Invalid sellingPlanId:', sellingPlanId, 'for plan:', quizData.selectedPlan);
          }
        } else {
          console.log('Adding item without selling_plan (one-time or no plan)');
        }
        
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        })
          .then(r => {
            if (!r.ok) {
              return r.json().then(err => {
                console.error('Cart add error response:', err);
                return Promise.reject(new Error(err.message || err.description || 'Add failed'));
              });
            }
            return r.json();
          });
      }
      
      /**
       * Optional: Debounced auto-update cart (DISABLED BY DEFAULT)
       * Only runs in edit mode and when not submitting
       * To enable: set ENABLE_AUTO_UPDATE = true below
       */
      const ENABLE_AUTO_UPDATE = false; // Set to true to enable auto-updates
      const AUTO_UPDATE_DELAY = 1500; // 1.5 second debounce
      
      function debouncedAutoUpdate() {
        // Clear existing timeout
        if (autoUpdateTimeout) {
          clearTimeout(autoUpdateTimeout);
        }
        
        // Only auto-update if enabled, in edit mode, and not submitting
        if (!ENABLE_AUTO_UPDATE || !isEditMode || isSubmitting || !cartItemKey) {
          return;
        }
        
        // Set new timeout
        autoUpdateTimeout = setTimeout(() => {
          if (isSubmitting || !isEditMode || !cartItemKey) return;
          
          // Sync form data
          syncFormData();
          save();
          
          // Build properties
          const properties = buildProperties();
          
          // Auto-update only updates properties (cannot change selling_plan)
          // If plan changed, user must manually submit
          updateCartItem(properties)
            .then(() => {
              console.log('Cart auto-updated successfully (properties only)');
            })
            .catch(err => {
              console.error('Cart auto-update failed:', err);
              // Silently fail - user can manually update
            });
        }, AUTO_UPDATE_DELAY);
      }
      
      // ============================================
      // CART CHECKING
      // ============================================
      
      // Check cart for existing item
      function checkCart() {
        return fetch('/cart.js')
          .then(r => r.json())
          .then(cart => {
            const item = cart.items.find(i => 
              i.variant_id.toString() === variantId.toString() || 
              i.product_handle === 'paw-parents-club-box'
            );
            
            if (item) {
              existingCartItem = item;
              cartItemKey = item.key;
              const extracted = extractFromCart(item);
              quizData = { ...quizData, ...extracted };
              
              // CRITICAL: Determine plan from cart item's selling_plan
              // This is the source of truth - what's actually in the cart
              const itemSellingPlanId = getItemSellingPlanId(item) || null;
              
              // Convert sellingPlans to strings for comparison
              const monthlyId = String(sellingPlans['monthly'] || '').trim();
              const sixMonthId = String(sellingPlans['6-month'] || '').trim();
              const twelveMonthId = String(sellingPlans['12-month'] || '').trim();
              
              // Determine plan type from selling_plan ID
              let detectedPlan = 'one-time';
              if (itemSellingPlanId) {
                if (monthlyId && itemSellingPlanId === monthlyId) {
                  detectedPlan = 'monthly';
                } else if (sixMonthId && itemSellingPlanId === sixMonthId) {
                  detectedPlan = '6-month';
                } else if (twelveMonthId && itemSellingPlanId === twelveMonthId) {
                  detectedPlan = '12-month';
                } else {
                  // Couldn't match - log warning but use extracted plan as fallback
                  console.warn('Could not match selling_plan ID:', itemSellingPlanId, 'to any known plan. Available:', {
                    monthly: monthlyId,
                    '6-month': sixMonthId,
                    '12-month': twelveMonthId
                  });
                  // Use extracted plan if available, otherwise keep as one-time
                  if (extracted.selectedPlan && extracted.selectedPlan !== 'one-time') {
                    detectedPlan = extracted.selectedPlan;
                  }
                }
              }
              
              // Update quizData with detected plan
              quizData.selectedPlan = detectedPlan;
              
              // Set sellingPlanId and originalSellingPlanId based on what's in cart
              if (itemSellingPlanId && detectedPlan !== 'one-time') {
                quizData.sellingPlanId = itemSellingPlanId;
                quizData.originalSellingPlanId = itemSellingPlanId; // This is what's in cart
              } else {
                quizData.sellingPlanId = '';
                quizData.originalSellingPlanId = 'one-time'; // No selling_plan = one-time
              }
              
              save();
              
              // Enable edit mode
              setEditMode(true);
              
              // Populate all steps
              quizSteps.forEach(step => populateStep(step));
              
              // Populate email and plan (even if steps are not active)
              populateEmail();
              populatePlan();
              
              // Show plan selection
              const planIndex = getStepIndex('plan-selection');
              if (planIndex !== -1) showStep(planIndex);
              
              // Ensure email and plan are populated after showing step
              setTimeout(() => {
                populateEmail();
                populatePlan();
              }, 300);
              
              return true;
            } else {
              // Item not in cart - disable edit mode
              setEditMode(false);
              return false;
            }
            return false;
          })
          .catch(err => {
            console.error('Error checking cart:', err);
            return false;
          });
      }
      
      // Event Handlers
      
      // Pet type selection
      container.querySelectorAll('.quiz-option--pet').forEach(btn => {
        btn.addEventListener('click', function() {
          quizData.petType = this.dataset.pet;
          save();
          const nextStep = findStep(this.dataset.nextStep);
          if (nextStep) showStep(getStepIndex(nextStep.dataset.step));
        });
      });
      
      // Option buttons
      container.querySelectorAll('.quiz-option:not(.quiz-option--pet)').forEach(btn => {
        btn.addEventListener('click', function() {
          const step = this.closest('.quiz-step');
          const stepId = step.dataset.step;
          
          // Remove selected from siblings
          step.querySelectorAll('.quiz-option').forEach(b => b.classList.remove('selected'));
          this.classList.add('selected');
          
          quizData.answers[stepId] = this.dataset.value;
          save();
          
          // Optional: Trigger debounced auto-update (if enabled)
          debouncedAutoUpdate();
          
          // Show conditional
          const conditionalId = this.dataset.showConditional;
          if (conditionalId) {
            container.querySelectorAll('.quiz__conditional').forEach(c => c.style.display = 'none');
            const conditional = container.querySelector('#' + conditionalId);
            if (conditional) conditional.style.display = 'block';
          }
          
          // Enable next
          const nextBtn = step.querySelector('.quiz-button--next');
          if (nextBtn) nextBtn.disabled = false;
        });
      });
      
      // Text inputs
      container.querySelectorAll('input[type="text"], input[type="date"], input[type="email"], textarea').forEach(input => {
        input.addEventListener('input', function() {
          const step = this.closest('.quiz-step');
          if (!step) return;
          const stepId = step.dataset.step;
          const name = this.name || this.id;
          
          if (this.type === 'email' && (stepId === 'dog-9' || stepId === 'cat-8')) {
            quizData.email = this.value.trim();
          } else if (name.includes('_notes')) {
            quizData.answers[name] = this.value.trim();
          } else {
            quizData.answers[stepId] = this.value.trim();
          }
          
          save();
          updatePetNamePlaceholders();
          
          // Optional: Trigger debounced auto-update (if enabled)
          debouncedAutoUpdate();
          
          // Enable next if required
          if (this.hasAttribute('required')) {
            const nextBtn = step.querySelector('.quiz-button--next');
            if (nextBtn) nextBtn.disabled = !this.value.trim();
          }
        });
      });
      
      // Checkboxes
      container.querySelectorAll('input[type="checkbox"]').forEach(cb => {
        cb.addEventListener('change', function() {
          const step = this.closest('.quiz-step');
          const stepId = step.dataset.step;
          
          if (!quizData.answers[stepId]) quizData.answers[stepId] = [];
          
          if (this.checked) {
            if (this.value === 'none') {
              step.querySelectorAll('input[type="checkbox"]').forEach(c => {
                if (c !== this) c.checked = false;
              });
              quizData.answers[stepId] = ['none'];
            } else {
              quizData.answers[stepId] = quizData.answers[stepId].filter(v => v !== 'none');
              if (!quizData.answers[stepId].includes(this.value)) {
                quizData.answers[stepId].push(this.value);
              }
            }
          } else {
            quizData.answers[stepId] = quizData.answers[stepId].filter(v => v !== this.value);
          }
          
          // Remove duplicates
          quizData.answers[stepId] = quizData.answers[stepId].filter((v, i, arr) => arr.indexOf(v) === i);
          if (quizData.answers[stepId].length === 0) delete quizData.answers[stepId];
          
          save();
          
          // Optional: Trigger debounced auto-update (if enabled)
          debouncedAutoUpdate();
        });
      });
      
      // Next buttons
      container.querySelectorAll('.quiz-button--next').forEach(btn => {
        btn.addEventListener('click', function() {
          const step = this.closest('.quiz-step');
          const stepId = step.dataset.step;
          const currentIndex = getStepIndex(stepId);
          
          if (stepId === 'dog-9' || stepId === 'cat-8') {
            const planIndex = getStepIndex('plan-selection');
            if (planIndex !== -1) showStep(planIndex);
          } else {
            const activeSteps = getActiveSteps();
            const currentActiveIndex = activeSteps.indexOf(step);
            if (currentActiveIndex < activeSteps.length - 1) {
              const nextStep = activeSteps[currentActiveIndex + 1];
              showStep(getStepIndex(nextStep.dataset.step));
            }
          }
        });
      });
      
      // Back buttons
      container.querySelectorAll('.quiz-button--back').forEach(btn => {
        btn.addEventListener('click', function() {
          const step = this.closest('.quiz-step');
          const stepId = step.dataset.step;
          
          if (stepId === 'plan-selection') {
            const emailStep = quizData.petType === 'dog' ? 'dog-9' : 'cat-8';
            const emailIndex = getStepIndex(emailStep);
            if (emailIndex !== -1) showStep(emailIndex);
            return;
          }
          
          const activeSteps = getActiveSteps();
          const currentActiveIndex = activeSteps.indexOf(step);
          if (currentActiveIndex > 0) {
            const prevStep = activeSteps[currentActiveIndex - 1];
            showStep(getStepIndex(prevStep.dataset.step));
          }
        });
      });
      
      // Plan selection
      container.querySelectorAll('.quiz-plan').forEach(plan => {
        plan.addEventListener('click', function() {
          container.querySelectorAll('.quiz-plan').forEach(p => p.classList.remove('selected'));
          this.classList.add('selected');
          
          // Update selected plan and selling plan ID
          quizData.selectedPlan = this.dataset.plan;
          // Get selling plan ID from data attribute or fallback to sellingPlans object
          const planSellingPlanId = this.dataset.sellingPlan || sellingPlans[quizData.selectedPlan] || '';
          quizData.sellingPlanId = planSellingPlanId;

          save();
          
          // Optional: Trigger debounced auto-update (if enabled)
          debouncedAutoUpdate();
          
          const submitBtn = container.querySelector('.quiz-button--submit');
          if (submitBtn) submitBtn.disabled = false;
        });
      });
      
      // Submit button
      const submitBtn = container.querySelector('.quiz-button--submit');
      if (submitBtn) {
        submitBtn.addEventListener('click', function() {
          if (!quizData.selectedPlan) {
            alert('Please select a plan');
            return;
          }
          
          // Ensure subscription selections carry a selling plan ID
          const selectedSellingPlanId = quizData.selectedPlan === 'one-time'
            ? ''
            : (quizData.sellingPlanId || sellingPlans[quizData.selectedPlan] || '');
          
          if (quizData.selectedPlan !== 'one-time' && !selectedSellingPlanId) {
            alert('Please choose a valid subscription option before continuing.');
            return;
          }
          
          // Prevent duplicate submissions
          if (isSubmitting) {
            return;
          }
          
          // Cancel any pending auto-update
          if (autoUpdateTimeout) {
            clearTimeout(autoUpdateTimeout);
            autoUpdateTimeout = null;
          }
          
          // Set submitting flag
          isSubmitting = true;
          
          // Show loader
          const originalText = this.textContent;
          this.disabled = true;
          this.textContent = 'Loading...';
          this.style.opacity = '0.7';
          
          // CRITICAL: Always sync form data before cart operations
          syncFormData();
          
          // Save to localStorage with latest data
          save();
          
          // Build properties from synced data
          const properties = buildProperties();
          const sellingPlanId = selectedSellingPlanId;
          
          // Determine original plan ID for comparison
          // Normalize to trimmed strings for reliable comparisons
          const originalPlanId = (quizData.originalSellingPlanId || '').toString().trim();
          
          // For new plan: if one-time, use 'one-time', otherwise use selling plan ID (as string)
          // If sellingPlanId is empty but plan is not one-time, try to get it from sellingPlans
          let newPlanId;
          if (quizData.selectedPlan === 'one-time') {
            newPlanId = 'one-time';
          } else {
            newPlanId = sellingPlanId
              ? String(sellingPlanId).trim()
              : (sellingPlans[quizData.selectedPlan] ? String(sellingPlans[quizData.selectedPlan]).trim() : '');
          }
          
          // Detect if selling plan changed
          // Compare: original plan ID vs new plan ID (both as strings for consistency)
          const isPlanChanged = String(originalPlanId) !== String(newPlanId);
          
          // Cart operation decision tree
          let cartPromise;
          
          if (!isEditMode || !cartItemKey) {
            // ADD MODE: New item
            cartPromise = addCartItem(properties, sellingPlanId);
          } else if (isEditMode && !isPlanChanged) {
            // EDIT MODE + PLAN UNCHANGED: Update properties only
            cartPromise = updateCartItem(properties);
          } else {
            // EDIT MODE + PLAN CHANGED: Remove and re-add
            cartPromise = removeCartItem() // Don't clear data - we're re-adding
              .then(() => {
                // Clear cart item key after removal (but keep localStorage and quizData for re-adding)
                cartItemKey = null;
                existingCartItem = null;
                // After removal, add with new selling plan (or no plan for one-time)
                return addCartItem(properties, sellingPlanId);
              })
              .then(() => {
                // CRITICAL: /cart/add.js returns only the line item, NOT the full cart
                // We need to fetch /cart.js to get the full cart and find our item
                return fetch('/cart.js')
                  .then(r => r.json())
                  .then(fullCart => {
                    // Find the newly added item in the full cart
                    const newItem = fullCart.items.find(i => 
                      i.variant_id.toString() === variantId.toString() || 
                      i.product_handle === 'paw-parents-club-box'
                    );
                    
                    if (newItem) {
                      existingCartItem = newItem;
                      cartItemKey = newItem.key;
                      // CRITICAL: Update original plan ID for future comparisons
                      // This must match what's actually in the cart
                      const newItemPlanId = getItemSellingPlanId(newItem);
                      if (newItemPlanId) {
                        quizData.originalSellingPlanId = newItemPlanId;
                        quizData.sellingPlanId = newItemPlanId;
                        // Also update selectedPlan to match the actual cart item
                        const planIdStr = newItemPlanId;
                        const monthlyId = String(sellingPlans['monthly'] || '');
                        const sixMonthId = String(sellingPlans['6-month'] || '');
                        const twelveMonthId = String(sellingPlans['12-month'] || '');
                        
                        if (planIdStr === monthlyId && monthlyId) {
                          quizData.selectedPlan = 'monthly';
                        } else if (planIdStr === sixMonthId && sixMonthId) {
                          quizData.selectedPlan = '6-month';
                        } else if (planIdStr === twelveMonthId && twelveMonthId) {
                          quizData.selectedPlan = '12-month';
                        }
                      } else {
                        quizData.originalSellingPlanId = 'one-time';
                        quizData.sellingPlanId = '';
                        quizData.selectedPlan = 'one-time';
                      }
                        // Sync email from the new cart item
                        const newEmailKey = Object.keys(newItem.properties || {}).find(k => k && k.trim().toLowerCase() === 'email');
                        if (newEmailKey) {
                          quizData.email = newItem.properties[newEmailKey] || '';
                        }
                      save();
                    } else {
                      console.error('New item not found in cart after re-add!');
                    }
                    
                    return fullCart;
                  });
              });
          }
          
          cartPromise
            .then(() => {
              // Success - reset flag before redirect
              isSubmitting = false;
              this.textContent = isEditMode ? 'Updated!' : 'Added!';
              setTimeout(() => {
                window.location.href = '/cart';
              }, 1000);
            })
            .catch(err => {
              // Error - restore button state and reset flag
              console.error('Cart operation error:', err);
              isSubmitting = false;
              this.textContent = originalText;
              this.disabled = false;
              this.style.opacity = '1';
              alert('Sorry, we could not ' + (isEditMode ? 'update' : 'add') + ' the cart. Please try again.');
            });
        });
      }
      
      // Fetch and display prices
      function loadPrices() {
        const variantId = container.dataset.variantId || '47525292605540';
        const plans = container.querySelectorAll('.quiz-plan');
        
        // Fallback prices (will be used if API fails)
        const fallbackPrices = {
          'one-time': 119,
          'monthly': 109,
          '6-month': 599,
          '12-month': 999
        };
        
        // Try to fetch product data with selling plans
        fetch('/products/paw-parents-club-box.json')
          .then(response => {
            if (!response.ok) throw new Error('Product not found');
            return response.json();
          })
          .then(productData => {
            // Find the variant
            const variant = productData.product.variants.find(v => v.id.toString() === variantId.toString());
            if (!variant) {
              throw new Error('Variant not found');
            }
            
            // Get product price (one-time)
            // Shopify JSON API returns price in the actual currency unit (AED), not cents
            // Example: 119.00 AED is returned as 119.00
            const productPrice = parseFloat(variant.price);
            
            // Update one-time price (use product price from Shopify)
            const oneTimePlan = container.querySelector('.quiz-plan[data-plan="one-time"]');
            if (oneTimePlan) {
              const priceElement = oneTimePlan.querySelector('.quiz-plan__price-amount');
              if (priceElement) {
                // Format price: round to whole number and add currency
                priceElement.textContent = Math.round(productPrice) + ' AED';
              }
            }
            
            // Use product price as base for subscription calculations
            const basePrice = productPrice;
            
            // Get selling plan groups from variant
            const sellingPlanGroups = variant.selling_plan_groups || [];
            
            // Map selling plan IDs to prices
            const planPriceMap = {};
            
            sellingPlanGroups.forEach(group => {
              group.selling_plans.forEach(sellingPlan => {
                const planId = sellingPlan.id.toString();
                let price = basePrice;
                
                // Calculate price based on adjustments
                if (sellingPlan.price_adjustments && sellingPlan.price_adjustments.length > 0) {
                  const adjustment = sellingPlan.price_adjustments[0];
                  if (adjustment.value_type === 'percentage') {
                    price = basePrice * (1 - adjustment.value / 100);
                  } else if (adjustment.value_type === 'fixed_amount') {
                    // Fixed amount is also in currency units, not cents
                    price = basePrice - parseFloat(adjustment.value);
                  } else if (adjustment.value_type === 'price') {
                    // Price is already in currency units
                    price = parseFloat(adjustment.value);
                  }
                }
                
                planPriceMap[planId] = price;
              });
            });
            
            // Update subscription plan prices
            plans.forEach(plan => {
              const planType = plan.dataset.plan;
              const sellingPlanId = plan.dataset.sellingPlan;
              
              if (planType === 'one-time') {
                return; // Already handled
              }
              
              const priceElement = plan.querySelector('.quiz-plan__price-amount');
              if (!priceElement) return;
              
              // Try to get price from planPriceMap
              if (sellingPlanId && planPriceMap[sellingPlanId]) {
                priceElement.textContent = planPriceMap[sellingPlanId].toFixed(0) + ' AED';
              } else {
                // Fallback to hardcoded prices
                if (fallbackPrices[planType]) {
                  priceElement.textContent = fallbackPrices[planType] + ' AED';
                }
              }
            });
          })
          .catch(error => {
            console.error('Error fetching product data:', error);
            // Use fallback prices
            plans.forEach(plan => {
              const planType = plan.dataset.plan;
              const priceElement = plan.querySelector('.quiz-plan__price-amount');
              if (priceElement && fallbackPrices[planType]) {
                priceElement.textContent = fallbackPrices[planType] + ' AED';
              }
            });
          });
      }
      
      // Initialize
      checkCart().then(hasItem => {
        if (!hasItem) {
          showStep(0);
          updatePetNamePlaceholders();
        }
        // Load prices after initialization
        loadPrices();
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPawParentsQuiz);
  } else {
    initPawParentsQuiz();
  }

  document.addEventListener('shopify:section:load', initPawParentsQuiz);
})();
